<template>
  <div class="modal" :class="{ 'is-active': open }" role="dialog" aria-modal="true">
    <div class="modal-background" @click="$emit('close')"></div>
    <div class="modal-card forbrug-invoice-modal">
      <header class="modal-card-head">
        <p class="modal-card-title">Regnings-mails</p>
        <button
          type="button"
          class="delete"
          aria-label="Luk"
          @click="$emit('close')"
        ></button>
      </header>
      <section class="modal-card-body">
        <div v-if="loadError" class="notification is-danger">{{ loadError }}</div>
        <p v-if="loading" class="mb-3">Henter skabelon…</p>

        <template v-if="!loading && !loadError">
          <p class="mb-3">
            Skabelonen flettes pr. aktiv husstand (kørsel for
            <strong>{{ periods.drivingMonthLabel }}</strong>,
            abonnement for
            <strong>{{ periods.subscriptionMonthLabel }}</strong>).
            Mails sendes til dig
            <template v-if="adminEmail"> (<strong>{{ adminEmail }}</strong>)</template>,
            så du kan videresende dem.
          </p>
          <div
            v-if="diagnostics && diagnostics.env !== 'dev' && !diagnostics.smtpConfigured"
            class="notification is-warning"
          >
            SMTP er ikke konfigureret (<code>$smtpUser</code>/<code>$smtpPass</code> i
            <code>.env/config.php</code>). Uden Simply <code>websmtp.simply.com</code> bliver mails ikke sendt korrekt.
          </div>

          <div class="field">
            <label class="label is-small">Emne</label>
            <div class="control">
              <input class="input" type="text" :value="subject" readonly>
            </div>
          </div>

          <div class="field">
            <label class="label is-small">Tekst (markdown + pladsholdere)</label>
            <div class="control">
              <textarea
                v-model="bodyTemplate"
                class="textarea forbrug-invoice-modal__body"
                rows="16"
              ></textarea>
            </div>
            <p class="help">
              Markdown: <code>**fed**</code>, <code>*kursiv*</code>, <code>~~gennemstreget~~</code>,
              <code>`kode`</code>, <code>[tekst](url)</code>, lister med <code>-</code>, linje med <code>---</code>.
            </p>
          </div>

          <div v-if="sendResult" class="notification" :class="sendResult.success ? 'is-success' : 'is-warning'">
            {{ sendResultMessage }}
          </div>
          <div v-if="sendError" class="notification is-danger">{{ sendError }}</div>

          <div class="forbrug-invoice-modal__send">
            <button
              type="button"
              class="button is-link"
              :disabled="sending || !accounts.length"
              @click="sendMails"
            >
              {{ sending ? 'Sender…' : 'Send mails til mig' }}
            </button>
            <span v-if="accounts.length" class="help">
              {{ accounts.length }} aktive husstande
            </span>
          </div>

          <details class="forbrug-invoice-modal__links mt-4">
            <summary>Vis forbrug-links (kopiér enkeltvis)</summary>
            <div v-if="!accounts.length" class="notification is-light mt-3">
              Ingen aktive husstande fundet.
            </div>
            <table v-else class="table is-fullwidth is-striped is-hoverable mt-3">
              <thead>
                <tr>
                  <th>Husstand</th>
                  <th>Link</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="account in accounts" :key="account.guid">
                  <td class="forbrug-invoice-modal__name">{{ account.name }}</td>
                  <td class="forbrug-invoice-modal__url">
                    <code :title="account.link">{{ account.link }}</code>
                  </td>
                  <td class="forbrug-invoice-modal__action">
                    <button
                      type="button"
                      class="button is-small"
                      :class="{ 'is-success': copiedGuid === account.guid }"
                      @click="copyUrl(account)"
                    >
                      {{ copiedGuid === account.guid ? 'Kopieret' : 'Kopiér URL' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </details>
        </template>
      </section>
      <footer class="modal-card-foot">
        <button type="button" class="button" @click="$emit('close')">Luk</button>
      </footer>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { fetchInvoiceMailPreview, sendInvoiceMails } from '@/forbrug/consumptionApi'

export default defineComponent({
  name: 'ForbrugInvoiceMailsModal',
  props: {
    open: { type: Boolean, default: false }
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      loadError: '',
      subject: '',
      bodyTemplate: '',
      periods: {},
      adminEmail: '',
      accounts: [],
      diagnostics: null,
      sending: false,
      sendError: '',
      sendResult: null,
      copiedGuid: '',
      copiedTimer: null
    }
  },
  computed: {
    sendResultMessage() {
      if (!this.sendResult) return ''
      const sent = this.sendResult.sent || 0
      const failed = this.sendResult.failed || 0
      if (this.sendResult.success) {
        return `Sendt ${sent} mail${sent === 1 ? '' : 's'} til ${this.sendResult.adminEmail || 'dig'}.`
      }
      return `Sendt ${sent}, fejlede ${failed}.`
    }
  },
  watch: {
    open(isOpen) {
      document.documentElement.classList.toggle('is-clipped', isOpen)
      if (isOpen) {
        this.loadPreview()
      } else {
        this.clearCopied()
        this.sendResult = null
        this.sendError = ''
      }
    }
  },
  beforeUnmount() {
    document.documentElement.classList.remove('is-clipped')
    this.clearCopied()
  },
  methods: {
    async loadPreview() {
      this.loading = true
      this.loadError = ''
      this.sendResult = null
      this.sendError = ''
      try {
        const data = await fetchInvoiceMailPreview()
        this.subject = data.subject
        this.bodyTemplate = data.bodyTemplate
        this.periods = data.periods || {}
        this.adminEmail = data.adminEmail || ''
        this.accounts = data.accounts || []
        this.diagnostics = data.diagnostics || null
      } catch (error) {
        this.loadError = (error && error.message) || 'Kunne ikke hente skabelon.'
        this.accounts = []
        this.diagnostics = null
      } finally {
        this.loading = false
      }
    },
    async sendMails() {
      if (this.sending || !this.accounts.length) return
      const count = this.accounts.length
      const confirmed = window.confirm(
        `Send ${count} regnings-mail${count === 1 ? '' : 's'} til ${this.adminEmail || 'din e-mail'}?`
      )
      if (!confirmed) return

      this.sending = true
      this.sendError = ''
      this.sendResult = null
      try {
        this.sendResult = await sendInvoiceMails(this.bodyTemplate)
      } catch (error) {
        this.sendError = (error && error.message) || 'Kunne ikke sende mails.'
      } finally {
        this.sending = false
      }
    },
    copyUrl(account) {
      const url = account.link
      const showCopied = () => {
        this.clearCopied()
        this.copiedGuid = account.guid
        this.copiedTimer = setTimeout(() => {
          this.copiedGuid = ''
          this.copiedTimer = null
        }, 2000)
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(showCopied).catch(() => {
          this.fallbackCopy(url, showCopied)
        })
        return
      }
      this.fallbackCopy(url, showCopied)
    },
    fallbackCopy(url, onSuccess) {
      const input = document.createElement('textarea')
      input.value = url
      input.setAttribute('readonly', '')
      input.style.position = 'absolute'
      input.style.left = '-9999px'
      document.body.appendChild(input)
      input.select()
      try {
        document.execCommand('copy')
        onSuccess()
      } catch (e) {
        // ignore
      }
      document.body.removeChild(input)
    },
    clearCopied() {
      if (this.copiedTimer) {
        clearTimeout(this.copiedTimer)
        this.copiedTimer = null
      }
      this.copiedGuid = ''
    }
  }
})
</script>

<style scoped>
.forbrug-invoice-modal {
  width: min(44rem, 96vw);
}

.forbrug-invoice-modal__body {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.85rem;
  white-space: pre-wrap;
}

.forbrug-invoice-modal__send {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.forbrug-invoice-modal__links summary {
  cursor: pointer;
  font-weight: 600;
}

.forbrug-invoice-modal__name {
  white-space: nowrap;
  font-weight: 600;
}

.forbrug-invoice-modal__url {
  max-width: 22rem;
}

.forbrug-invoice-modal__url code {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.8rem;
}

.forbrug-invoice-modal__action {
  white-space: nowrap;
  text-align: right;
  width: 1%;
}
</style>
